/**
 * Calculate working days between two dates, excluding weekends and holidays
 * @param startDate - Start date string
 * @param endDate - End date string
 * @param holidays - Array of holidays to exclude
 * @returns Number of working days
 */
export const calculateWorkingDays = (
  startDate: string, 
  endDate: string, 
  holidays: any[] = []
): number => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Check if dates are valid
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }
  
  if (end < start) return 0;
  
  let workingDays = 0;
  const currentDate = new Date(start);
  
  // Create a function to check if a date falls within any holiday range
  const isHoliday = (date: Date) => {
    return holidays
      .filter(h => h.isActive)
      .some(h => {
        const holidayStart = new Date(h.startDate);
        const holidayEnd = new Date(h.endDate);
        holidayStart.setHours(0, 0, 0, 0);
        holidayEnd.setHours(23, 59, 59, 999);
        return date >= holidayStart && date <= holidayEnd;
      });
  };
  
  while (currentDate <= end) {
    const dayOfWeek = currentDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHolidayDate = isHoliday(currentDate);
    
    // Only count weekdays that are not holidays
    if (!isWeekend && !isHolidayDate) {
      workingDays++;
    }
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return workingDays;
};

export const getAvailableYears = (rangeBefore = 5, rangeAfter = 1): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = currentYear + rangeAfter; year >= currentYear - rangeBefore; year--) {
    years.push(year);
  }
  return years;
};

export const isRequestInYear = (startDate: string, year: number): boolean => {
  return new Date(startDate).getFullYear() === year;
};

export const overlapsYear = (startDate: string, endDate: string, year: number): boolean => {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
  const start = new Date(startDate);
  const end = new Date(endDate);
  return start <= yearEnd && end >= yearStart;
};
